<script setup lang="ts">
import { ALIconButton } from '@af-lib/vue-ui';
import { computed, inject, reactive, ref } from 'vue';

import { BYTES_PER_MB } from '@/enums/constants';
import type { Translate } from '@/types/i18n/i18n.types';

type Props = {
  size: number;
  maxFilesUpload?: number;
  datacyPrefix: string;
};
const props = withDefaults(defineProps<Props>(), {
  maxFilesUpload: 1
});

const emit = defineEmits<{
  imageUpload: [value: Blob[]];
}>();

const t = inject('t') as Translate;

const errorMessage = ref('');
const files = reactive<{ src: string; type: string; name: string; file: Blob }[]>([]);

const limitFilesReached = computed(() => files.length === props.maxFilesUpload);

const getTotalSize = (fileList: Blob[]) => fileList.reduce((acc, file) => acc + file.size, 0);

const handleFileUpload = async (e: Event) => {
  const { files: newFiles } = e.target as HTMLInputElement;
  errorMessage.value = '';
  if (!newFiles) return;

  let totalSize = getTotalSize(files.map(f => f.file));

  for (let i = 0; i < newFiles.length; i++) {
    const file = newFiles[i];
    if (!file) continue;

    if (files.length >= props.maxFilesUpload) {
      errorMessage.value = t('error.user.photo.file_limit', {
        file: props.maxFilesUpload
      });
      break;
    }
    if (!file.type.includes('image')) {
      errorMessage.value = t('error.user.photo.file_format');
      continue;
    }

    if (file.size > props.size || totalSize + file.size > props.size) {
      errorMessage.value = t('error.user.photo.file_size', { size: props.size / BYTES_PER_MB });
      return;
    }

    const imgUrl = URL.createObjectURL(file);
    const sanitizedFile = new File([file], file.name.replaceAll(' ', '_'), { type: file.type });
    const blob = new Blob([sanitizedFile], { type: sanitizedFile.type });

    files.push({
      src: imgUrl,
      type: file.type,
      name: sanitizedFile.name,
      file: blob
    });

    totalSize += file.size;
  }

  emitAllImages();
};

const handleFileDrop = async (event: DragEvent) => {
  const { dataTransfer } = event;
  if (dataTransfer?.files) {
    await handleFileUpload({ target: { files: dataTransfer.files } } as unknown as Event);
  }
};

const emitAllImages = () => {
  const fileList = files.map(f => f.file);
  emit('imageUpload', fileList);
};

function deleteImage(index: number) {
  files.splice(index, 1);
  errorMessage.value = '';
  emitAllImages();
}
</script>

<template>
  <div class="d-grid gap-8">
    <div class="card text-af-grey-800" :class="{ 'opacity-50': limitFilesReached }">
      <v-sheet
        height="95"
        width="100%"
        color="grey-lighten-4"
        class="d-flex align-center rounded justify-center position-relative"
        :data-cy="`${props.datacyPrefix}-upload`"
        @dragover.prevent
        @drop.prevent="handleFileDrop"
      >
        <div class="upload-image d-flex item-center justify-center text-af-grey-800">
          <v-btn
            tag="label"
            for="multi-img-file"
            rounded="xs"
            variant="text"
            class="upload"
            :disabled="limitFilesReached"
          >
            <template #default>
              <div class="d-grid gap-8 text-body-4 font-weigth-regular">
                <v-icon icon="fa-down-to-line" class="ms-auto me-auto text-icon-3" />
                {{
                  limitFilesReached
                    ? t('general.image.limit_reached')
                    : t('general.media.upload.body_web', { image: props.maxFilesUpload })
                }}
              </div>
            </template>
          </v-btn>
          <input
            id="multi-img-file"
            class="img-file"
            type="file"
            name="file"
            accept=".jpg, .jpeg, .png, .svg"
            multiple
            :disabled="limitFilesReached"
            @change="handleFileUpload"
          />
        </div>
      </v-sheet>
    </div>

    <div class="d-grid gap-8">
      <div
        v-for="(file, index) in files"
        :key="index"
        class="d-flex align-center justify-space-between image-preview position-relative"
      >
        <img :src="file.src" alt="" class="uploaded-image" />

        <p class="text-body-5 font-weight-regular text-ellipsis">{{ file.name }}</p>

        <ALIconButton
          variant="secondary"
          icon="xmark"
          size="xs"
          class="remove-btn"
          :label="t('general.remove')"
          tooltipPosition="left"
          @click="deleteImage(index)"
        />
      </div>
    </div>

    <p v-if="errorMessage" class="text-errorText">
      <v-icon icon="fat fa-info-circle" class="text-icon-4"></v-icon>
      <span class="text-body-4 ms-2 d-inline">{{ errorMessage }}</span>
    </p>
  </div>
</template>

<style scoped lang="scss">
.card {
  border-radius: 4px;
  border: 1px dashed rgb(var(--v-theme-focusBorder));
  max-width: 485px;
  overflow-x: hidden;
}

.action-image {
  position: absolute;
  top: 4px;
  inset-inline-end: 4px;
  display: flex;
  gap: 8px;
}

.upload-image,
.upload {
  width: 100%;
  height: 100%;
}

.uploaded-image {
  height: 31px;
  width: 44px;
  border: 1px solid rgb(var(--v-theme-border));
  border-radius: 4px;
}

.img-file {
  display: none;
}

.image-preview {
  position: relative;
}

.text-ellipsis {
  text-overflow: ellipsis;
  max-width: 230px;
  white-space: nowrap;
  overflow: hidden;
}
</style>
